import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import ProductForm from 'components/forms/ProductForm';
import ConfirmAction from 'components/popups/ConfirmAction';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';

/**
 * productType 1 - Admission
 * productType 2 - Add-on
 * productType 3 - Assigned Seating (some features disabled)
 */

export default function ProductEditor(props) {
    const { productId, productType, hostId, eventId, onEdit, onDelete, onCancel } = props;

    // *** Feature Flags *** //
    const enablePOS = productType !== 3;
    const enableQuantity = productType !== 3;
    const enableMultiScan = productType !== 3;
    const enableSeatPricing = productType === 3;

    const [loading, setLoading] = useState(false); // Used when updating a product
    const [confirmDelete, setConfirmDelete] = useState(false);

    const [init, setInit] = useState({
        status: 'loading', // loading, error, success
        enableSponsor: false,
        deletable: false,
        defaultValues: null,
    });

    const { notify } = useNotification();

    useEffect(() => {
        (async () => {
            try {
                // ***** Fetch Product, Event, and Colors ***** //

                const [productResponse, eventResponse, colorResponse, productGroupsResponse] =
                    await Promise.all([
                        fetchExpresso(`/apiv2/products/${productId}`),
                        fetchExpresso(`/apiv2/events/${eventId}`),
                        fetchExpresso(`/apiv2/product_color`),
                        fetchExpresso(`/apiv2/events/${eventId}/product_groups`),
                    ]);

                if (
                    productResponse.status !== 200 ||
                    eventResponse.status !== 200 ||
                    colorResponse.status !== 200 ||
                    productGroupsResponse.status !== 200
                ) {
                    throw new Error();
                }

                const product = await productResponse.json();
                const event = await eventResponse.json();
                const { colors } = await colorResponse.json();
                const { groups: productGroups } = await productGroupsResponse.json();

                let audience;

                if (product.access_code) {
                    audience = 'accessCode';
                } else if (product.visible === 1) {
                    audience = 'visible';
                } else {
                    audience = 'hidden';
                }

                let posVisible = 'hidden';
                if (product.pos_visible === 1) posVisible = 'visible';

                // ***** Get Seat Pricing ***** //

                let seatPricing = [];

                if (enableSeatPricing) {
                    const seatPricingRequest = await fetchExpresso(
                        `/apiv2/events/${eventId}/seat-categories`,
                    );
                    if (seatPricingRequest.status !== 200) throw new Error();
                    const seatPricingResult = await seatPricingRequest.json();

                    seatPricing = seatPricingResult.categories.map((category) => ({
                        priceId: null,
                        key: category.key,
                        name: category.name,
                        enabled: false,
                        price: product.seat_pricing.length === 0 ? (product.price ?? '') : '',
                    }));

                    for (const price of product.seat_pricing) {
                        for (const sp of seatPricing) {
                            if (sp.key === price.seat_category_id) {
                                sp.enabled = true;
                                sp.price = price.price;
                                sp.priceId = price.price_id; // Set the priceId so we can update the record if needed
                            }
                        }
                    }
                }

                setInit({
                    status: 'success',
                    enableSponsor: event.sponsor_module_flag === 1,
                    attendeeRegistration: event.checkout_question_mode === 2,
                    isAttendee: Boolean(product.is_attendee),
                    deletable: product.bundle_count === 0 && product.quantity_sold === 0,
                    labelColors: colors,
                    productGroups: productGroups ?? [],
                    defaultValues: {
                        name: product.prod_name,
                        price: product.price,
                        free: !product.price,
                        seatPricing: seatPricing,
                        description: product.prod_desc,
                        startDate: moment(product.scan_start_date),
                        endDate: moment(product.scan_end_date),
                        eventStartDate: moment(event.start_date),
                        eventEndDate: moment(event.end_date),
                        labelMessage: product.label,
                        labelColor: product.label_color_id,
                        saleStartDate: moment(product.sale_start_date),
                        saleEndDate: moment(product.sale_end_date),
                        quantity: product.qty_total_available,
                        minOrder: product.qty_min_purchase,
                        maxOrder: product.qty_max_purchase,
                        overrideDates: Boolean(product.scan_date_override),
                        multipleScan: Boolean(product.multi_day_entry),
                        unlimitedEntry: Boolean(product.unlimited_entry),
                        image: product.sponsor_image_url,
                        audience: audience,
                        accessCode: product.access_code,
                        quantitySold: product.quantity_sold,
                        posVisible: posVisible,
                        posName: product.pos_prod_name,
                        posPrice: product.pos_price,
                        productGroupId: product.prod_group_id,
                        isAttendee: Boolean(product.is_attendee),
                    },
                    createdAtMsg: `Created ${getRelativeTime(product.created_at)} by ${product.created_by}`,
                    updatedAtMsg:
                        `Updated ${getRelativeTime(product.updated_at)}` +
                        (product.updated_by ? ` by ${product.updated_by}` : ''),
                });
            } catch (e) {
                setInit({
                    status: 'error',
                    defaultValues: null,
                });
            }
        })();
    }, [productId, eventId, enableSeatPricing]);

    const handleEdit = (formData) => {
        if (loading) return;
        setLoading(true);

        // Format and set Actions for Seat Pricing
        if (enableSeatPricing) {
            formData.seatPricing = formData.seatPricing.map((category) => {
                let action = 'NONE';
                if (category.priceId && category.enabled === true) action = 'UPDATE';
                if (category.priceId && category.enabled === false) action = 'DELETE';
                if (!category.priceId && category.enabled === true) action = 'CREATE';

                return {
                    priceId: category.priceId,
                    key: category.key,
                    price: category.enabled ? category.price : 0,
                    action: action,
                };
            });
        }

        fetchExpresso(`/apiv2/products/${productId}`, {
            method: 'PUT',
            body: formData,
        })
            .then((res) => {
                if (res.status === 200) {
                    notify.success('Ticket Updated');
                    onEdit({
                        name: formData.name,
                        isAttendee: Boolean(formData.isAttendee),
                        previousIsAttendee: init.isAttendee,
                    });
                } else {
                    notify.error('There was a problem updating your ticket');
                    setLoading(false);
                }
            })
            .catch(() => {
                notify.error('There was a problem updating your ticket');
                setLoading(false);
            });
    };

    const handleDelete = () => {
        setConfirmDelete(false);
        setLoading(true);

        fetchExpresso(`/apiv2/products/${productId}`, {
            method: 'DELETE',
        })
            .then(async (res) => {
                if (res.status === 200) {
                    notify.success('Deleted ticket');
                    onDelete(productId);
                } else {
                    const data = await res.json();
                    notify.error(data.message ?? 'There was a problem deleting your ticket');
                    setLoading(false);
                }
            })
            .catch(() => {
                notify.error('There was a problem deleting your ticket');
                setLoading(false);
            });
    };

    // Waiting for default values to load
    if (init.status === 'loading') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    // Error loading data for product form
    if (init.status === 'error') {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <Typography align="center">There was a problem loading your form</Typography>
            </Box>
        );
    }

    return (
        <>
            <ProductForm
                editMode
                deletable={init.deletable}
                defaultValues={init.defaultValues}
                createdAtMsg={init.createdAtMsg}
                updatedAtMsg={init.updatedAtMsg}
                enablePOS={enablePOS}
                enableQuantity={enableQuantity}
                enableMultiScan={enableMultiScan}
                enableSeatPricing={enableSeatPricing}
                enableGroups={productType !== 3 && (init.productGroups?.length ?? 0) > 0}
                enableSponsor={init.enableSponsor}
                attendeeRegistration={init.attendeeRegistration}
                labelColors={init.labelColors}
                productGroups={init.productGroups}
                onSubmit={handleEdit}
                onDelete={() => setConfirmDelete(true)}
                onCancel={onCancel}
                hostId={hostId}
                eventId={eventId}
                loading={loading}
            />

            <ConfirmAction
                open={confirmDelete}
                onConfirm={handleDelete}
                onCancel={() => setConfirmDelete(false)}
                destructive
                title="Are you sure you want to delete this ticket?"
                description="You cannot recover a ticket once it is deleted"
                confirmText="Delete"
            />
        </>
    );
}
